import {useMutation, useQueryClient} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
  parseJSONWithBetterErrors,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

import {useReviewRequestsQueryKey} from '../components/overview/data/route-queries'
import {useMergeBoxQueryKey} from '@github-ui/mergebox/use-merge-box-query-key'
import type {CopilotReviewEffortRequest} from '@github-ui/pull-request-page-data-tooling/copilot-review-effort'
import {useSharedCopilotReviewEffort} from '../components/overview/reviewers/copilot-review-effort/use-shared-copilot-review-effort'
import {useCopilotReviewEffortFailures} from './use-copilot-review-effort-failures'

export type ReviewerModification = {
  reviewerId: number
  isTeam: boolean
}

export type MutationData = {
  addReviewers: ReviewerModification[]
  removeReviewers: ReviewerModification[]
  copilotReviewEffortRequest?: CopilotReviewEffortRequest
}

type Callbacks = {
  onError?: (error: Error) => void
  onSuccess?: () => void
}

export function useModifyReviewers({onError, onSuccess}: Callbacks = {}) {
  const reviewRequestsQueryKey = useReviewRequestsQueryKey()
  const mergeBoxQueryKey = useMergeBoxQueryKey()
  const apiURL = usePageDataUrl(PageData.modifyReviewers)
  const queryClient = useQueryClient()
  const effort = useSharedCopilotReviewEffort()
  const {dismissFailures, failRequest} = useCopilotReviewEffortFailures()

  return useMutation({
    mutationFn: async ({copilotReviewEffortRequest, ...data}: MutationData) => {
      const request =
        copilotReviewEffortRequest ??
        (data.addReviewers.some(reviewer => !reviewer.isTeam && reviewer.reviewerId === effort?.reviewerId)
          ? effort?.captureRequest()
          : undefined)
      dismissFailures()
      let json
      try {
        const response = await fetchWithErrorHandling(apiURL, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
          },
          body: {
            ...data,
            ...request?.fields,
          },
        })
        json = await parseJSONWithBetterErrors(response)
        throwErrorsIfBadResponse(response, json)
      } catch (error) {
        failRequest(request)
        throw error
      }
      try {
        request?.acceptResponse(json)
      } catch (error) {
        reportError(error)
      }
      return json
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({queryKey: reviewRequestsQueryKey}, {cancelRefetch: false}),
        queryClient.invalidateQueries({queryKey: mergeBoxQueryKey}, {cancelRefetch: false}),
      ])
      onSuccess?.()
    },
    onError: (e: Error) => {
      onError?.(e)
    },
  })
}
